import { createFileRoute } from "@tanstack/react-router";
import {
  LegalH2,
  LegalH3,
  LegalH4,
  LegalLink,
  LegalP,
  LegalPageLayout,
  LegalSection,
  LegalUL,
} from "components/legal-page-layout";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPolicyPage,
});

function PrivacyPolicyPage() {
  return (
    <LegalPageLayout title="Privacy Policy" lastUpdated="May 6, 2025">
      <LegalP>
        This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your
        information when You use the Service and tells You about Your privacy rights and how the law protects You.
      </LegalP>
      <LegalP>
        We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection
        and use of information in accordance with this Privacy Policy.
      </LegalP>

      <LegalSection>
        <LegalH2>Interpretation and Definitions</LegalH2>
        <LegalH3>Interpretation</LegalH3>
        <LegalP>
          The words of which the initial letter is capitalized have meanings defined under the following conditions. The
          following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
        </LegalP>
        <LegalH3>Definitions</LegalH3>
        <LegalP>For the purposes of this Privacy Policy:</LegalP>
        <LegalUL>
          <li>
            <strong>Account</strong> means a unique account created for You to access our Service or parts of our
            Service.
          </li>
          <li>
            <strong>Affiliate</strong> means an entity that controls, is controlled by or is under common control with a
            party, where &quot;control&quot; means ownership of 50% or more of the shares, equity interest or other
            securities entitled to vote for election of directors or other managing authority.
          </li>
          <li>
            <strong>Application</strong> refers to Autonoma AI, the software program provided by the Company.
          </li>
          <li>
            <strong>Company</strong> (referred to as either &quot;the Company&quot;, &quot;We&quot;, &quot;Us&quot; or
            &quot;Our&quot; in this Agreement) refers to Autonoma AI Inc., 251 Little Falls Drive, Wilmington, New
            Castle County, Delaware 19808, United States.
          </li>
          <li>
            <strong>Cookies</strong> are small files that are placed on Your computer, mobile device or any other device
            by a website, containing the details of Your browsing history on that website among its many uses.
          </li>
          <li>
            <strong>Country</strong> refers to: Delaware, United States.
          </li>
          <li>
            <strong>Device</strong> means any device that can access the Service such as a computer, a cellphone or a
            digital tablet.
          </li>
          <li>
            <strong>Personal Data</strong> is any information that relates to an identified or identifiable individual.
          </li>
          <li>
            <strong>Service</strong> refers to the Application or the Website or both.
          </li>
          <li>
            <strong>Service Provider</strong> means any natural or legal person who processes the data on behalf of the
            Company. It refers to third-party companies or individuals employed by the Company to facilitate the
            Service, to provide the Service on behalf of the Company, to perform services related to the Service or to
            assist the Company in analyzing how the Service is used.
          </li>
          <li>
            <strong>Usage Data</strong> refers to data collected automatically, either generated by the use of the
            Service or from the Service infrastructure itself (for example, the duration of a page visit).
          </li>
          <li>
            <strong>Website</strong> refers to Autonoma AI, accessible from{" "}
            <LegalLink href="https://autonoma.app">autonoma.app</LegalLink>.
          </li>
          <li>
            <strong>You</strong> means the individual accessing or using the Service, or the company, or other legal
            entity on behalf of which such individual is accessing or using the Service, as applicable.
          </li>
        </LegalUL>
      </LegalSection>

      <LegalSection>
        <LegalH2>Collecting and Using Your Personal Data</LegalH2>
        <LegalH3>Types of Data Collected</LegalH3>
        <LegalH4>Personal Data</LegalH4>
        <LegalP>
          While using Our Service, We may ask You to provide Us with certain personally identifiable information that
          can be used to contact or identify You. Personally identifiable information may include, but is not limited
          to:
        </LegalP>
        <LegalUL>
          <li>Email address</li>
          <li>First name and last name</li>
          <li>Usage Data</li>
        </LegalUL>
        <LegalH4>Usage Data</LegalH4>
        <LegalP>Usage Data is collected automatically when using the Service.</LegalP>
        <LegalP>
          Usage Data may include information such as Your Device&apos;s Internet Protocol address (e.g. IP address),
          browser type, browser version, the pages of our Service that You visit, the time and date of Your visit, the
          time spent on those pages, unique device identifiers and other diagnostic data.
        </LegalP>
        <LegalP>
          When You access the Service by or through a mobile device, We may collect certain information automatically,
          including, but not limited to, the type of mobile device You use, Your mobile device unique ID, the IP address
          of Your mobile device, Your mobile operating system, the type of mobile Internet browser You use, unique
          device identifiers and other diagnostic data.
        </LegalP>
        <LegalP>
          We may also collect information that Your browser sends whenever You visit our Service or when You access the
          Service by or through a mobile device.
        </LegalP>
        <LegalH4>Information Collected while Using the Application</LegalH4>
        <LegalP>
          While using Our Application, in order to provide features of Our Application, We may collect, with Your prior
          permission:
        </LegalP>
        <LegalUL>
          <li>Information regarding your location</li>
        </LegalUL>
        <LegalP>
          We use this information to provide features of Our Service, to improve and customize Our Service. The
          information may be uploaded to the Company&apos;s servers and/or a Service Provider&apos;s server or it may be
          simply stored on Your device.
        </LegalP>
        <LegalP>You can enable or disable access to this information at any time, through Your Device settings.</LegalP>
        <LegalH4>Tracking Technologies and Cookies</LegalH4>
        <LegalP>
          We use Cookies and similar tracking technologies to track the activity on Our Service and store certain
          information. Tracking technologies used are beacons, tags, and scripts to collect and track information and to
          improve and analyze Our Service. The technologies We use may include:
        </LegalP>
        <LegalUL>
          <li>
            <strong>Cookies or Browser Cookies.</strong> A cookie is a small file placed on Your Device. You can
            instruct Your browser to refuse all Cookies or to indicate when a Cookie is being sent. However, if You do
            not accept Cookies, You may not be able to use some parts of our Service. Unless you have adjusted Your
            browser setting so that it will refuse Cookies, our Service may use Cookies.
          </li>
          <li>
            <strong>Web Beacons.</strong> Certain sections of our Service and our emails may contain small electronic
            files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit
            the Company, for example, to count users who have visited those pages or opened an email and for other
            related website statistics.
          </li>
        </LegalUL>
        <LegalP>
          Cookies can be &quot;Persistent&quot; or &quot;Session&quot; Cookies. Persistent Cookies remain on Your
          personal computer or mobile device when You go offline, while Session Cookies are deleted as soon as You close
          Your web browser.
        </LegalP>
        <LegalP>We use both Session and Persistent Cookies for the purposes set out below:</LegalP>
        <LegalUL>
          <li>
            <strong>Necessary / Essential Cookies.</strong> Type: Session Cookies. Administered by: Us. Purpose: These
            Cookies are essential to provide You with services available through the Website and to enable You to use
            some of its features. They help to authenticate users and prevent fraudulent use of user accounts.
          </li>
          <li>
            <strong>Cookies Policy / Notice Acceptance Cookies.</strong> Type: Persistent Cookies. Administered by: Us.
            Purpose: These Cookies identify if users have accepted the use of cookies on the Website.
          </li>
          <li>
            <strong>Functionality Cookies.</strong> Type: Persistent Cookies. Administered by: Us. Purpose: These
            Cookies allow us to remember choices You make when You use the Website, such as remembering your login
            details or language preference.
          </li>
        </LegalUL>
        <LegalH3>Use of Your Personal Data</LegalH3>
        <LegalP>The Company may use Personal Data for the following purposes:</LegalP>
        <LegalUL>
          <li>
            <strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.
          </li>
          <li>
            <strong>To manage Your Account:</strong> to manage Your registration as a user of the Service.
          </li>
          <li>
            <strong>For the performance of a contract:</strong> the development, compliance and undertaking of the
            purchase contract for the products, items or services You have purchased or of any other contract with Us
            through the Service.
          </li>
          <li>
            <strong>To contact You:</strong> by email, telephone calls, SMS, or other equivalent forms of electronic
            communication regarding updates or informative communications related to the functionalities, products or
            contracted services.
          </li>
          <li>
            <strong>To provide You</strong> with news, special offers and general information about other goods,
            services and events which we offer, unless You have opted not to receive such information.
          </li>
          <li>
            <strong>To manage Your requests:</strong> To attend and manage Your requests to Us.
          </li>
          <li>
            <strong>For business transfers:</strong> We may use Your information to evaluate or conduct a merger,
            divestiture, restructuring, reorganization, dissolution, or other sale or transfer of some or all of Our
            assets.
          </li>
          <li>
            <strong>For other purposes:</strong> such as data analysis, identifying usage trends, determining the
            effectiveness of our promotional campaigns and to evaluate and improve our Service, products, services,
            marketing and your experience.
          </li>
        </LegalUL>
        <LegalP>We may share Your personal information in the following situations:</LegalP>
        <LegalUL>
          <li>
            <strong>With Service Providers:</strong> to monitor and analyze the use of our Service, to contact You.
          </li>
          <li>
            <strong>For business transfers:</strong> in connection with, or during negotiations of, any merger, sale of
            Company assets, financing, or acquisition of all or a portion of Our business to another company.
          </li>
          <li>
            <strong>With Affiliates:</strong> in which case we will require those affiliates to honor this Privacy
            Policy.
          </li>
          <li>
            <strong>With business partners:</strong> to offer You certain products, services or promotions.
          </li>
          <li>
            <strong>With other users:</strong> when You share personal information or otherwise interact in public areas
            with other users.
          </li>
          <li>
            <strong>With Your consent:</strong> for any other purpose with Your consent.
          </li>
        </LegalUL>
        <LegalH3>Retention of Your Personal Data</LegalH3>
        <LegalP>
          The Company will retain Your Personal Data only for as long as is necessary for the purposes set out in this
          Privacy Policy, to comply with our legal obligations, resolve disputes, and enforce our legal agreements and
          policies.
        </LegalP>
        <LegalP>
          The Company will also retain Usage Data for internal analysis purposes, generally for a shorter period of
          time, except when this data is used to strengthen the security or to improve the functionality of Our Service,
          or We are legally obligated to retain this data for longer time periods.
        </LegalP>
        <LegalH3>Transfer of Your Personal Data</LegalH3>
        <LegalP>
          Your information, including Personal Data, is processed at the Company&apos;s operating offices and in any
          other places where the parties involved in the processing are located. This information may be transferred to
          and maintained on computers located outside of Your state, province, country or other governmental
          jurisdiction where the data protection laws may differ from those from Your jurisdiction.
        </LegalP>
        <LegalP>
          Your consent to this Privacy Policy followed by Your submission of such information represents Your agreement
          to that transfer. The Company will take all steps reasonably necessary to ensure that Your data is treated
          securely and in accordance with this Privacy Policy.
        </LegalP>
        <LegalH3>Delete Your Personal Data</LegalH3>
        <LegalP>
          You have the right to delete or request that We assist in deleting the Personal Data that We have collected
          about You. You may update, amend, or delete Your information at any time by signing in to Your Account and
          visiting the account settings section, or by contacting Us. Please note that We may need to retain certain
          information when we have a legal obligation or lawful basis to do so.
        </LegalP>
        <LegalH3>Disclosure of Your Personal Data</LegalH3>
        <LegalH4>Business Transactions</LegalH4>
        <LegalP>
          If the Company is involved in a merger, acquisition or asset sale, Your Personal Data may be transferred. We
          will provide notice before Your Personal Data is transferred and becomes subject to a different Privacy
          Policy.
        </LegalP>
        <LegalH4>Law enforcement</LegalH4>
        <LegalP>
          Under certain circumstances, the Company may be required to disclose Your Personal Data if required to do so
          by law or in response to valid requests by public authorities.
        </LegalP>
        <LegalH4>Other legal requirements</LegalH4>
        <LegalP>
          The Company may disclose Your Personal Data in the good faith belief that such action is necessary to:
        </LegalP>
        <LegalUL>
          <li>Comply with a legal obligation</li>
          <li>Protect and defend the rights or property of the Company</li>
          <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
          <li>Protect the personal safety of Users of the Service or the public</li>
          <li>Protect against legal liability</li>
        </LegalUL>
        <LegalH3>Security of Your Personal Data</LegalH3>
        <LegalP>
          The security of Your Personal Data is important to Us, but remember that no method of transmission over the
          Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means
          to protect Your Personal Data, We cannot guarantee its absolute security.
        </LegalP>
      </LegalSection>

      <LegalSection>
        <LegalH2>Children&apos;s Privacy</LegalH2>
        <LegalP>
          Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable
          information from anyone under the age of 13. If You are a parent or guardian and You are aware that Your child
          has provided Us with Personal Data, please contact Us.
        </LegalP>
      </LegalSection>

      <LegalSection>
        <LegalH2>Links to Other Websites</LegalH2>
        <LegalP>
          Our Service may contain links to other websites that are not operated by Us. We strongly advise You to review
          the Privacy Policy of every site You visit. We have no control over and assume no responsibility for the
          content, privacy policies or practices of any third party sites or services.
        </LegalP>
      </LegalSection>

      <LegalSection>
        <LegalH2>Changes to this Privacy Policy</LegalH2>
        <LegalP>
          We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new
          Privacy Policy on this page and updating the &quot;Last updated&quot; date above. You are advised to review
          this Privacy Policy periodically for any changes.
        </LegalP>
      </LegalSection>

      <LegalSection>
        <LegalH2>Contact Us</LegalH2>
        <LegalP>If you have any questions about this Privacy Policy, You can contact us:</LegalP>
        <LegalUL>
          <li>By email: support@autonoma.app</li>
        </LegalUL>
      </LegalSection>
    </LegalPageLayout>
  );
}
